import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { AutoComplete } from "primereact/autocomplete";
import { Employee } from "../../../../../models/DSF/OrganogramAndAppointments";
import { Node } from "reactflow";

interface SearchSuggestion {
  id: string;
  label: string;
  type: "department" | "employee";
  subtitle?: string;
  nodeId: string;
}

interface SearchBoxProps {
  nodes: Node[];
  onResultSelect: (nodeId: string) => void;
  onSearchChange: (term: string) => void;
  searchTerm: string;
}

const SearchBox: React.FC<SearchBoxProps> = ({
  nodes,
  onResultSelect,
  onSearchChange,
  searchTerm,
}) => {
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<SearchSuggestion[]>([]);

  // Generate suggestions from nodes
  useEffect(() => {
    const allSuggestions: SearchSuggestion[] = [];

    nodes.forEach(node => {
      const { data } = node;

      // Add department/role suggestions
      allSuggestions.push({
        id: `dept-${node.id}`,
        label: data.label,
        type: "department",
        subtitle: data.accessLevelName || data.title,
        nodeId: node.id,
      });

      // Add employee suggestions
      if (data.employees && data.employees.length > 0) {
        data.employees.forEach((employee: Employee) => {
          allSuggestions.push({
            id: `emp-${employee.id}`,
            label: `${employee.name} ${employee.surname}`,
            type: "employee",
            subtitle: employee.title || "",
            nodeId: node.id,
          });
        });
      }
    });

    setSuggestions(allSuggestions);
  }, [nodes]);

  // Filter suggestions based on search term
  const searchSuggestions = (event: any) => {
    const query = event.query.toLowerCase();
    const filtered = suggestions
      .filter(
        suggestion =>
          suggestion.label.toLowerCase().includes(query) ||
          (suggestion.subtitle && suggestion.subtitle.toLowerCase().includes(query))
      )
      .slice(0, 10); // Limit to 10 suggestions

    setFilteredSuggestions(filtered);
  };

  const handleSelect = (suggestion: SearchSuggestion) => {
    onSearchChange(suggestion.label);
    onResultSelect(suggestion.nodeId);
  };

  const handleClear = () => {
    onSearchChange("");
  };

  const itemTemplate = (suggestion: SearchSuggestion) => {
    return (
      <div className="search-suggestion-item">
        <div className="suggestion-main">
          <i className={`pi ${suggestion.type === "employee" ? "pi-user" : "pi-sitemap"}`} />
          <span className="suggestion-label">{suggestion.label}</span>
        </div>
        {suggestion.subtitle && <div className="suggestion-subtitle">{suggestion.subtitle}</div>}
      </div>
    );
  };

  return (
    <div className="enhanced-search-box">
      <AutoComplete
        value={searchTerm}
        suggestions={filteredSuggestions}
        completeMethod={searchSuggestions}
        onSelect={e => handleSelect(e.value)}
        onChange={e => onSearchChange(e.value || "")}
        itemTemplate={itemTemplate}
        placeholder="Search employees, roles, or departments..."
        className="search-autocomplete"
        panelClassName="search-suggestions-panel"
        dropdown
        forceSelection={false}
        style={{ minWidth: "350px" }}
      />
      {searchTerm && (
        <Button
          icon="pi pi-times"
          className="p-button-text p-button-sm search-clear-btn"
          onClick={handleClear}
          tooltip="Clear search"
        />
      )}
    </div>
  );
};

export default SearchBox;
